import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { Box, Tabs, Tab } from "@mui/material";
import PropTypes from "prop-types";
import LearningCard from "../../../Pages/HomePage/LearningCard";
import {
  getCoursesByHub,
  getLearnerCourses,
} from "../../../Redux/CourseDetails";
import {
  isCourseEligible,
  getCoursesGroupedByCategoryAndModule,
} from "utils/foundationUnlock";
import "./corehubsubtab.css";
import useFetchImageUrls from "../../../ConstantUseFetchImageUrls";
import { coreHubTabAssets } from "../../../imagesData/ImagesData";

/* TAB PANEL COMPONENT */
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

/* MAIN COMPONENT */
export default function BasicTabs({ hubName }) {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  const [activeTab, setActiveTab] = useState("");
  const [moduleIndex, setModuleIndex] = useState(0);
  const [enterpriseId, setEnterpriseId] = useState(null);
  const [value, setValue] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [userLevel, setUserLevel] = useState(null);
  const [isHubLoading, setIsHubLoading] = useState(true);

  const cardsPerPage = 6;

  const query = useMemo(() => {
    return `params=${encodeURIComponent(JSON.stringify(coreHubTabAssets))}`;
  }, []);

  const { imageUrls, error } = useFetchImageUrls(query);
  const urls = imageUrls?.imageUrl || {};

  /* MEDAL ICONS */
  const medalIcons = {
    bronze: urls["bronze"],
    silver: urls["silver"],
    gold: urls["gold"],
    platinum: urls["platinum"],
    diamond: urls["diamond"],
    disabled: urls["disabled"],
  };

  /* -------------------- REDUX DATA -------------------- */
  const userData = useSelector((state) => state.userDetails.userData[0]);

  const { coursesByHub, categoryModulesByHub, moduleBadges } = useSelector(
    (state) => state.courseDetails,
  );

  /* -------------------- HUB DATA -------------------- */
  const hubData = coursesByHub?.find((hub) => hub.hubName === hubName);
  const hubCourses = hubData?.courses || [];
  const categoryModules = categoryModulesByHub?.[hubName] || {};

  /* UI HELPERS */
  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  const handleChange = (event, newValue) => setValue(newValue);
  const handleActiveTab = (value) => setActiveTab(value);

  const courseImg = [
    { img: urls["course1"] },
    { img: urls["course2"] },
    { img: urls["course3"] },
    { img: urls["course4"] },
    { img: urls["course5"] },
    { img: urls["course1"] },
    { img: urls["course2"] },
    { img: urls["course3"] },
    { img: urls["course4"] },
  ];

  const getRandomImageForCourse = (courseId) => {
    const index = courseId % courseImg.length;
    return courseImg[index];
  };

  /* ELIGIBLE COURSES GROUPING */
  const eligibleCoursesByCategoryAndModule = useMemo(() => {
    if (!hubCourses.length || !userData || !userLevel) return {};

    return getCoursesGroupedByCategoryAndModule({
      coursesByHub: [{ hubName, courses: hubCourses }],
      userData,
      userLevel,
      hubName,
    });
  }, [hubCourses, userData, userLevel]);

  /* TABS */
  const tabs = useMemo(
    () => Object.keys(eligibleCoursesByCategoryAndModule),
    [eligibleCoursesByCategoryAndModule],
  );

  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      setActiveTab(tabs[0]);
      setValue(0);
      setModuleIndex(0);
    }
  }, [tabs, activeTab]);

  const isModuleEligibleForBadge = (moduleName) => {
    const modCourses =
      eligibleCoursesByCategoryAndModule?.[activeTab]?.[moduleName];

    if (!modCourses) return false;

    const mandatory = modCourses.Mandatory || [];
    const optional = modCourses.Optional || [];

    // Case 1: Mandatory courses exist → all mandatory must be completed
    if (mandatory.length > 0) {
      return mandatory.every((c) => c.enrollmentDate && c.completionDate);
    }

    // Case 2: No mandatory courses → all optional must be completed
    if (optional.length > 0) {
      return optional.every((c) => c.enrollmentDate && c.completionDate);
    }

    // Case 3: No courses at all → no badge
    return false;
  };

  const modulesByCategory = useMemo(() => {
    const acc = {};

    Object.entries(eligibleCoursesByCategoryAndModule).forEach(
      ([category, modules]) => {
        acc[category] = Object.keys(modules); // keys are already sorted by indexing
      },
    );

    return acc;
  }, [eligibleCoursesByCategoryAndModule]);

  const eligibleActiveModules = useMemo(() => {
    const allModules = modulesByCategory[activeTab] || [];
    return allModules.map((moduleName, idx) => ({
      moduleName,
      completed: isModuleEligibleForBadge(moduleName),
      badge: categoryModules[activeTab]?.find(
        (m) => m.moduleName === moduleName,
      )?.badge,
      index: idx,
    }));
  }, [eligibleCoursesByCategoryAndModule, modulesByCategory, activeTab]);

  const completedBadges = eligibleActiveModules.filter(
    (m) => m.completed,
  ).length;

  useEffect(() => {
    setModuleIndex(0);
    setCurrentPage(1);
  }, [activeTab]);

  const activeModule = modulesByCategory[activeTab]?.[moduleIndex] || null;

  /* COURSES */
  const mandatoryHubCourses =
    eligibleCoursesByCategoryAndModule?.[activeTab]?.[activeModule]
      ?.Mandatory || [];

  const optionalHubCourses =
    eligibleCoursesByCategoryAndModule?.[activeTab]?.[activeModule]?.Optional ||
    [];

  const activeModuleCourses = [...mandatoryHubCourses, ...optionalHubCourses];

  const eligibleCategoryCourses = useMemo(() => {
    const data = eligibleCoursesByCategoryAndModule[activeTab] || {};
    return Object.values(data).flatMap((mod) => [
      ...(mod.Mandatory || []),
      ...(mod.Optional || []),
    ]);
  }, [eligibleCoursesByCategoryAndModule, activeTab]);

  const completedCourses = eligibleCategoryCourses.filter(
    (c) => c.enrollmentDate && c.completionDate,
  ).length;

  const maxCourses = eligibleCategoryCourses.length || 1;
  const totalModules = eligibleActiveModules.length;
  const completedModules = eligibleActiveModules.filter(
    (m) => m.completed,
  ).length;

  const progressPercent = Math.round((completedCourses / maxCourses) * 100);

  const earnedPoints = eligibleCategoryCourses.reduce(
    (sum, c) =>
      sum + (c.enrollmentDate && c.completionDate ? c.coursePoints || 0 : 0),
    0,
  );

  const totalCategoryPoints = eligibleCategoryCourses.reduce(
    (sum, c) => sum + (c.coursePoints || 0),
    0,
  );

  /* PAGINATION */
  const totalPages = Math.ceil(activeModuleCourses.length / cardsPerPage);

  const currentCards = activeModuleCourses.slice(
    (currentPage - 1) * cardsPerPage,
    currentPage * cardsPerPage,
  );

  const isEmpty = (value) =>
    value === null || value === undefined || value === "" || value === "NULL";
  const userLevelDes = activeAccount.idTokenClaims?.jobFamilyDescription;
  let userLevelDB = isEmpty(userLevelDes) ? userData?.level : userLevelDes;

  /* EFFECTS */
  useEffect(() => {
    if (!activeAccount) return;

    setEnterpriseId(activeAccount.idTokenClaims?.enterpriseID);
    setUserLevel(userLevelDB);
  }, [activeAccount]);

  const businessUnit = useMemo(
    () => userData?.businessUnit,
    [userData?.businessUnit],
  );

  useEffect(() => {
    if (!hubName || !businessUnit) return;

    dispatch(getCoursesByHub(hubName, businessUnit));
  }, [hubName, businessUnit, dispatch]);

  useEffect(() => {
    if (!enterpriseId || !businessUnit) return;

    dispatch(getLearnerCourses(enterpriseId, businessUnit));
  }, [enterpriseId, businessUnit, dispatch]);

  /* PROGRESS STEPS */
  const normalizeBadge = (badge) => badge?.toLowerCase().replace(/\s+/g, "");

  const progressSteps = useMemo(() => {
    const steps = [];

    eligibleActiveModules.forEach((m) => {
      steps.push({
        type: "dot",
        status: m.completed ? "done" : "pending",
      });

      const badgeKey = normalizeBadge(m.badge);

      steps.push({
        type: "medal",
        badge: m.badge,
        status: m.completed ? "done" : "pending",
        icon: m.completed ? medalIcons[badgeKey] : medalIcons.disabled,
      });
    });

    return steps;
  }, [eligibleActiveModules]);

  const progressWidth = useMemo(() => {
    if (!progressSteps.length) return 0;

    const doneMedals = progressSteps.filter(
      (s) => s.type === "medal" && s.status === "done",
    );

    if (!doneMedals.length) return 0;

    const lastDoneIndex = progressSteps.findLastIndex(
      (s) => s.type === "medal" && s.status === "done",
    );

    return Math.round((lastDoneIndex / (progressSteps.length - 1)) * 100);
  }, [progressSteps]);

  const remainingSteps = Math.max(totalModules - completedModules, 0);

  const finalBadge = useMemo(() => {
    if (!eligibleActiveModules.length) return null;
    return eligibleActiveModules[eligibleActiveModules.length - 1]?.badge;
  }, [eligibleActiveModules]);

  const nextCategory = useMemo(() => {
    if (!tabs.length || !activeTab) return null;
    const idx = tabs.indexOf(activeTab);
    return idx > -1 ? tabs[idx + 1] : null;
  }, [tabs, activeTab]);

  const nextCategoryFinalBadge = useMemo(() => {
    if (!nextCategory) return null;
    const modules = categoryModules?.[nextCategory];
    return modules?.[modules.length - 1]?.badge || null;
  }, [nextCategory, categoryModules]);

  const unlockedModuleIndexes = eligibleActiveModules.map((_, index) => index);

  const nextBadge = useMemo(() => {
    const nextModule = eligibleActiveModules.find((m) => !m.completed);
    return nextModule?.badge || null;
  }, [eligibleActiveModules]);

  const isProgressReady =
    activeTab && eligibleActiveModules.length > 0 && progressSteps.length > 0;

  useEffect(() => {
    if (activeTab && eligibleCoursesByCategoryAndModule[activeTab]) {
      setModuleIndex(0);
      setCurrentPage(1);
    }
  }, [eligibleCoursesByCategoryAndModule, activeTab]);

  useEffect(() => {
    setIsHubLoading(true);
    setIsExpanded(false); // optional but recommended
  }, [hubName]);

  useEffect(() => {
    if (
      hubCourses.length &&
      userData &&
      userLevel &&
      Object.keys(eligibleCoursesByCategoryAndModule).length
    ) {
      setIsHubLoading(false);
    }
  }, [hubCourses, userData, userLevel, eligibleCoursesByCategoryAndModule]);

  const isDataLoading =
    isExpanded &&
    (!hubCourses.length ||
      !userData ||
      !userLevel ||
      !Object.keys(eligibleCoursesByCategoryAndModule).length);

  if (error) return <div>Error loading images</div>;
  if (!imageUrls || Object.keys(imageUrls).length === 0) return null;

  /* JSX */
  return (
    <Box sx={{ width: "100%" }}>
      {tabs?.length > 0 ? (
        <Box
          onClick={toggleExpand}
          sx={{
            cursor: "pointer",
            color: "#A100FF",
            fontWeight: 600,
            mt: 5,
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          {isExpanded ? "↑ Collapse" : "↓ Expand"}
        </Box>
      ) : (
        <Box
          sx={{
            color: "#A100FF",
            fontWeight: 600,
            mt: 5,
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Coming Soon
        </Box>
      )}

      {isExpanded && isHubLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "240px",
            fontWeight: 600,
            color: "#A100FF",
            fontSize: "16px",
          }}
        >
          Loading courses for this hub…
        </Box>
      )}

      {isExpanded && !isHubLoading && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab}
                  label={tab}
                  {...a11yProps(index)}
                  onClick={() => handleActiveTab(tab)}
                />
              ))}
            </Tabs>
          </Box>

          {tabs.map((tab, index) => (
            <React.Fragment key={tab}>
              <CustomTabPanel value={value} index={index}>
                <div className="onestep-gold-wrap">
                  <div className="onestep-gold-row row1">
                    <div className="onestep-gold-box">
                      <h3>
                        {nextBadge
                          ? `You are 1 step away from the ${nextBadge} Badge`
                          : "Complete modules to earn your badge"}
                      </h3>
                      <p>
                        {nextCategory && nextCategoryFinalBadge
                          ? `Complete ${nextCategory} next to earn ${nextCategoryFinalBadge} badge`
                          : "Complete this hub to unlock the next category"}
                      </p>
                      {isProgressReady && (
                        <div className="progress-container">
                          {progressSteps.map((step, idx) => {
                            const icon = step.icon
                              ? step.icon
                              : step.status === "done"
                                ? step.doneicon
                                : step.status === "pending"
                                  ? step.pendingicon
                                  : null;

                            return (
                              <div
                                key={step.id}
                                className={`progress-step ${step.status}`}
                                style={{
                                  left: `${(idx / (progressSteps.length - 1)) * 100}%`,
                                }}
                              >
                                {icon ? (
                                  <img
                                    className="step-icon"
                                    src={icon}
                                    style={{
                                      height: "2rem",
                                      marginBottom: "0.5rem",
                                    }}
                                  />
                                ) : (
                                  <span className="step-dot" />
                                )}
                              </div>
                            );
                          })}
                          <div
                            className="progress-line"
                            style={{
                              background: `linear-gradient(
																to right,
																#A100FF ${progressWidth}%,
																#e5e7eb ${progressWidth}%
															)`,
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="onestep-gold-row row2">
                    <div className="onestep-gold-box">
                      <div className="courses-medals-points-box">
                        <div className="icon-holder">
                          <img
                            src={urls["completeIcon"]}
                            alt="icon"
                            className="core-hub-complete-icon"
                          />
                        </div>
                        <h3>
                          {progressPercent}%<span> Courses completed</span>
                        </h3>
                      </div>
                      <div className="courses-medals-points-box">
                        <div className="icon-holder">
                          <img
                            src={urls["medalIcon"]}
                            alt="icon"
                            className="core-hub-complete-icon"
                          />
                        </div>
                        <h3>
                          {completedBadges || 0}
                          <span> Medals earned</span>
                        </h3>
                      </div>
                      <div className="courses-medals-points-box">
                        <div className="icon-holder">
                          <img
                            src={urls["pointsIcon"]}
                            alt="icon"
                            className="core-hub-complete-icon"
                          />
                        </div>
                        <h3>
                          {earnedPoints}/{totalCategoryPoints}
                          <span> Points earned</span>
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </CustomTabPanel>

              {/* MODULE TABS */}
              {value === index && modulesByCategory[tab]?.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
                  <Tabs
                    value={moduleIndex}
                    onChange={(e, newVal) => {
                      setModuleIndex(newVal);
                      setCurrentPage(1); // reset page when switching module
                    }}
                    aria-label="Module Tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    {modulesByCategory[tab].map((modName, idx) => {
                      const isLocked = (() => {
                        if (idx === 0) return false; // first module always unlocked

                        // Check ALL previous modules
                        for (let i = 0; i < idx; i++) {
                          const prevModuleName = modulesByCategory[tab][i];
                          const prevModule =
                            eligibleCoursesByCategoryAndModule?.[tab]?.[
                              prevModuleName
                            ];

                          if (!prevModule) return true;

                          const mandatory = prevModule.Mandatory || [];

                          // If mandatory exist and not fully completed → lock
                          if (mandatory.length > 0) {
                            const allCompleted = mandatory.every(
                              (c) => c.enrollmentDate && c.completionDate,
                            );
                            if (!allCompleted) return true;
                          }
                        }

                        return false;
                      })();

                      return (
                        <Tab
                          key={`${tab}-${modName}`}
                          label={modName}
                          disabled={isLocked}
                          sx={{
                            opacity: isLocked ? 0.4 : 1,
                            cursor: isLocked ? "not-allowed" : "pointer",
                          }}
                        />
                      );
                    })}
                  </Tabs>
                </Box>
              )}

              {/* COURSE CARDS GRID */}
              {value === index && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  {currentCards.length > 0 ? (
                    currentCards.map((course) => (
                      <LearningCard
                        key={course.id}
                        course={course}
                        courseImg={getRandomImageForCourse(course.id)}
                      />
                    ))
                  ) : (
                    <Box sx={{ color: "#999", fontStyle: "italic" }}>
                      No courses available in this module.
                    </Box>
                  )}
                </Box>
              )}

              {value === index && totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: 2,
                    gap: 1,
                  }}
                >
                  {/* Left arrow */}
                  <button
                    onClick={() => handlePageClick(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      cursor: currentPage === 1 ? "default" : "pointer",
                      border: "none",
                      background: "none",
                      color: currentPage === 1 ? "#ccc" : "#333",
                      fontSize: "20px",
                      padding: "0 8px",
                    }}
                  >
                    &#8592;
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageClick(pageNum)}
                        style={{
                          cursor: "pointer",
                          border:
                            pageNum === currentPage
                              ? "2px solid black"
                              : "none",
                          background: "none",
                          color: "#000",
                          fontWeight: pageNum === currentPage ? "700" : "500",
                          padding: "2px 8px",
                          margin: "0 4px",
                          outline: "none",
                          userSelect: "none",
                        }}
                      >
                        {pageNum}
                      </button>
                    ),
                  )}

                  {/* Right arrow */}
                  <button
                    onClick={() => handlePageClick(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      cursor:
                        currentPage === totalPages ? "default" : "pointer",
                      border: "none",
                      background: "none",
                      color: currentPage === totalPages ? "#ccc" : "#333",
                      fontSize: "20px",
                      padding: "0 8px",
                    }}
                  >
                    &#8594;
                  </button>
                </Box>
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </Box>
  );
}
